import { useQueryClient } from '@tanstack/react-query';
import { ChangeEvent, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { RichMediaState, useRichInput } from '@bonusly/common-action-input';
import { useRichText } from '@bonusly/common-rich-text-editor';
import { useToast } from '@bonusly/design-system';
import { useDispatch } from '@bonusly/events';
import { useAnalytics, useCurrentUser } from '@bonusly/who-am-i';

import { useCreateFeedbackRequest } from '../../../hooks/use-feedback-api';
import { useFetchUsers } from '../../../hooks/use-fetch-users';
import { Feedback, User } from '../../../lib';
import { ReferenceEntity } from '../../../lib/entities/reference';
import { AmplitudeEvent } from '../../../types/amplitude-event';
import { Visibility } from '../../../types/feedback';
import { FeedbackEvent } from '../../../types/feedback-event';
import { QueryKey } from '../../../types/query-key';
import { createContent } from '../../../utils/content';
import { Toast } from '../../toast/toast';

interface UseFeedbackModalOptions {
	defaultText?: string;
	defaultUsers?: User[];
	isOpen?: boolean;
	onSuccess?: VoidFunction;
	reference?: ReferenceEntity;
}

export interface RequestFeedbackArgs {
	giverIds: string[];
	prompt: string;
}

export const useRequestModal = ({ defaultText, defaultUsers = [], isOpen, onSuccess, reference }: UseFeedbackModalOptions = {}) => {
	const focusRef = useRef(false);
	const resetRef = useRef(false);
	const { t } = useTranslation('feedback');
	const fetchRecipients = useFetchUsers();
	const toast = useToast();
	const { track } = useAnalytics();
	const [showReference, setShowReference] = useState(true);
	const [showAiAlert, setShowAiAlert] = useState(!!defaultText);
	const [visibility, setVisibility] = useState(Feedback.defaultVisibility);
	const currentUser = useCurrentUser();
	const dispatch = useDispatch();

	const content = useMemo(() => {
		if (!defaultUsers.length) {
			return undefined;
		}

		return createContent(...defaultUsers);
	}, [defaultUsers]);
	const queryClient = useQueryClient();

	const { editor: recipientEditor, uniqueId } = useRichInput({
		content,
		modules: ['recipient'],
		fetchRecipients,
		placeholder: t('feedback.request.user_placeholder'),
		options: {
			recipient: {
				matchWithoutTrigger: true,
			},
		},
	});

	const {
		editor: textEditor,
		html,
		text,
	} = useRichText({
		content: defaultText,
		placeholder: t('feedback.request.input_placeholder'),
	});

	const data = RichMediaState.parse(recipientEditor);
	const plainText = text.replace(/^\s+|\s+$/g, '');
	const isValid = plainText.length > 0 && data.recipients.length > 0;

	// Auto focus handling
	if (recipientEditor && textEditor && isOpen && !focusRef.current) {
		focusRef.current = true;

		if (data.recipientCount === 0) {
			recipientEditor?.commands.focus('end');
		} else {
			textEditor?.commands.focus('end');
		}
	}

	if (!isOpen) {
		focusRef.current = false;
	}

	// This sets a ref based on whether or not the user has made changes to the input.
	// Once the form is submitted, the ref is also set to false. This is used to prevent the
	// contents of the inputs from being reset when the modal is closed, unless the form is
	// submitted first.
	if (isOpen && (data.recipients.length > 0 || plainText.length > 0)) {
		resetRef.current = false;
	}

	const onCreateFeedbackSuccess = {
		onSuccess: () => {
			dispatch(FeedbackEvent.Created);
			toast({
				position: 'bottom-right',
				render: () => {
					return <Toast isCloseable message={t('feedback.request.success_message')} status="success" />;
				},
			});

			queryClient.invalidateQueries([QueryKey.ConsolidatedFeedbackIndex]);
			dispatch(FeedbackEvent.MessageChanged);

			resetRef.current = true;
			onSuccess?.();
		},
		onError: () => {
			toast({
				title: t('feedback.request.failure_message'),
				status: 'error',
			});
		},
	};

	const onSaveDraftSuccess = {
		onSuccess: () => {
			toast({
				position: 'bottom-right',
				render: () => {
					return <Toast isCloseable message={t('feedback.request.draft_success_message')} status="success" />;
				},
			});
			queryClient.invalidateQueries([QueryKey.ConsolidatedFeedbackIndex]);
			onSuccess?.();
		},
		onError: () => {
			toast({
				title: t('feedback.requested.failure_message_draft'),
				status: 'error',
			});
		},
	};
	const { isLoading, mutate: create } = useCreateFeedbackRequest();

	const onSubmit = ({ isDraft }: { isDraft: boolean }) => {
		if (!isValid) {
			return;
		}

		const userIds = data.recipients.map((recipient) => recipient.id);

		const params = {
			giverId: userIds[0],
			receiverId: currentUser.id,
			requestText: html,
			isDraft,
			reference,
			isSharedWithManager: visibility === Visibility.Immediate,
		};

		if (isDraft) {
			create(params, onSaveDraftSuccess);
			track(AmplitudeEvent.RequestFeedbackDraftSaved);
		} else {
			create(params, onCreateFeedbackSuccess);
			track(AmplitudeEvent.SendRequest);
		}
	};

	const onSubmitDraft = async (e: any) => {
		e.preventDefault();
		onSubmit({ isDraft: true });
	};

	const onSubmitRequest = async (e: any) => {
		e.preventDefault();
		onSubmit({ isDraft: false });
	};

	const onReset = () => {
		if (resetRef.current) {
			recipientEditor?.commands.clearContent();
			setVisibility(Visibility.Private);

			if (defaultText) {
				textEditor?.commands.setContent(defaultText);
				setShowAiAlert(true);
			} else {
				textEditor?.commands.clearContent();
			}
		}

		setShowReference(true);
	};

	const onRemoveReference = () => {
		setShowReference(false);
	};

	const onClearGeneratedText = () => {
		setShowAiAlert(false);
		textEditor?.commands.clearContent();
	};

	const onToggleVisibility = (e: ChangeEvent<HTMLInputElement>) => {
		setVisibility(e.target.checked ? Visibility.Immediate : Visibility.Private);
	};

	const showShareWithManager = currentUser.manager_id && data.recipients.some((recipient) => recipient.id !== currentUser.manager_id);

	return {
		onReset,
		onSubmitDraft,
		onSubmitRequest,
		isValid,
		isVisibilityChecked: visibility === Visibility.Immediate,
		isSubmitting: isLoading,
		onClearGeneratedText,
		onRemoveReference,
		onToggleVisibility,
		showAiAlert,
		showReference,
		textEditor,
		recipientEditor,
		uniqueId,
		showShareWithManager,
	};
};
